import React from 'react';
import { Box, Button, Skeleton, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { colors, typography } from '../../styles/theme';
import {
  metaAdsBodyTextSx, metaAdsCardSx, metaAdsCardTitleSx, metaAdsMutedTextSx, metaAdsSkeletonSx,
} from './metaAdsUi.styles';

/**
 * Piezas de presentación comunes de Análisis y Presupuesto: tarjeta con título, barras, dona,
 * tabla y filas «nombre completo → valor». Todas alinean sus columnas con una sola grilla
 * (cada fila usa `display: contents`), así nada queda desparejo ni con huecos.
 */

export const META_BAR_COLOR = colors.primary;
export const META_BAR_COLOR_LIGHT = colors.primaryLight;
export const META_BAR_COLOR_GRAY = '#7A7A7A';

const captionSx = { ...metaAdsMutedTextSx, fontSize: '12px', lineHeight: '16px' };
const dividerSx = '1px solid rgba(255,255,255,0.07)';

/** Tarjeta: título, texto chico a la derecha (`caption`) o un control (`action`). */
export function MetaCard({ title, caption = null, action = null, children, testId }) {
  return (
    <Box sx={metaAdsCardSx} data-testid={testId} component="section" aria-label={typeof title === 'string' ? title : undefined}>
      {title ? (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, minHeight: 24 }}>
          <Typography component="h3" sx={metaAdsCardTitleSx}>{title}</Typography>
          {action || (caption ? <Typography sx={{ ...captionSx, textAlign: 'right' }}>{caption}</Typography> : null)}
        </Box>
      ) : null}
      {children}
    </Box>
  );
}

/** Texto chico explicativo dentro de una tarjeta. */
export function MetaFootnote({ children, testId }) {
  return <Typography sx={captionSx} data-testid={testId}>{children}</Typography>;
}

/**
 * Estado de una tarjeta que depende de un pedido: cargando (skeleton) · «Todavía no disponible»
 * (la API de este entorno no lo tiene, sin error rojo) · error con «Reintentar» · contenido.
 * `skeleton`: placeholder con la forma del contenido (`MetaSkeletons`); sin él, `rows` líneas.
 */
export function MetaBlockState({
  status, error = null, onRetry = null, unavailableText = 'Todavía no disponible en este entorno.', testId, rows = 3, skeleton = null, children,
}) {
  if (status === 'loading' || status === 'idle') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }} aria-busy="true" data-testid={testId ? `${testId}-loading` : undefined}>
        {skeleton || Array.from({ length: rows }).map((_, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <Skeleton key={i} variant="rounded" height={16} width={i === rows - 1 ? '55%' : '100%'} sx={metaAdsSkeletonSx} />
        ))}
      </Box>
    );
  }
  if (status === 'unavailable') {
    return <Typography sx={metaAdsMutedTextSx} data-testid={testId ? `${testId}-unavailable` : undefined}>{unavailableText}</Typography>;
  }
  if (status === 'error') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }} data-testid={testId ? `${testId}-error` : undefined} role="alert">
        <Typography sx={{ ...metaAdsMutedTextSx, flex: 1, minWidth: 0 }}>{error || 'No pudimos cargar estos datos.'}</Typography>
        {onRetry ? (
          <Button size="small" onClick={onRetry} sx={{ textTransform: 'none', fontWeight: 600, color: colors.primaryLight }}>Reintentar</Button>
        ) : null}
      </Box>
    );
  }
  return children;
}

/** Mensaje amable cuando el pedido salió bien pero no hay filas. */
export function MetaEmptyLine({ children, testId }) {
  return <Typography sx={metaAdsMutedTextSx} data-testid={testId}>{children}</Typography>;
}

/**
 * Barras horizontales: nombre completo · barra · valor. `fraction` 0..1.
 * @param {{ rows: Array<{ key: string, label: string, valueText: string, fraction: number, tone?: 'primary'|'light'|'gray' }> }} props
 */
export function MetaBarList({ rows, testId, labelWidth = 'minmax(56px, max-content)' }) {
  const colorOf = (tone) => (tone === 'light' ? META_BAR_COLOR_LIGHT : tone === 'gray' ? META_BAR_COLOR_GRAY : META_BAR_COLOR);
  return (
    <Box
      component="ul"
      sx={{ m: 0, p: 0, listStyle: 'none', display: 'grid', gridTemplateColumns: `${labelWidth} minmax(40px, 1fr) max-content`, columnGap: 1.25, rowGap: 1.1, alignItems: 'center' }}
      data-testid={testId}
    >
      {rows.map((r) => (
        <Box component="li" key={r.key} sx={{ display: 'contents' }} data-testid={testId ? `${testId}-${r.key}` : undefined}>
          <Typography sx={{ ...metaAdsBodyTextSx, fontSize: '13px' }}>{r.label}</Typography>
          <Box sx={{ height: 8, borderRadius: '4px', bgcolor: 'rgba(255,255,255,0.08)', overflow: 'hidden' }} aria-hidden>
            <Box sx={{ height: '100%', width: `${Math.max(0, Math.min(1, r.fraction || 0)) * 100}%`, bgcolor: colorOf(r.tone), borderRadius: '4px' }} />
          </Box>
          <Typography sx={{ ...metaAdsBodyTextSx, fontSize: '13px', textAlign: 'right', whiteSpace: 'nowrap' }}>{r.valueText}</Typography>
        </Box>
      ))}
    </Box>
  );
}

// Segundo tono bien distinto del primero: Mujeres / Hombres tienen que leerse a simple vista.
const DONUT_COLORS = [META_BAR_COLOR, '#B9EFE9', META_BAR_COLOR_GRAY];

/**
 * Dona con leyenda. `segments`: `{ key, label, value, pct, detail }` (value > 0 para dibujarse).
 * La leyenda dice cada porcentaje: el gráfico solo acompaña.
 */
export function MetaDonut({ segments, ariaLabel, testId }) {
  const R = 40;
  const C = 2 * Math.PI * R;
  const total = segments.reduce((a, s) => a + (s.value || 0), 0);
  let offset = 0;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }} data-testid={testId}>
      <Box component="svg" viewBox="0 0 100 100" role="img" aria-label={ariaLabel} sx={{ width: 96, height: 96, flexShrink: 0, transform: 'rotate(-90deg)' }}>
        <circle cx="50" cy="50" r={R} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="18" />
        {total > 0 ? segments.map((s, i) => {
          const len = ((s.value || 0) / total) * C;
          const dash = `${len} ${C - len}`;
          const node = (
            <circle key={s.key} cx="50" cy="50" r={R} fill="none" stroke={DONUT_COLORS[i % DONUT_COLORS.length]} strokeWidth="18"
              strokeDasharray={dash} strokeDashoffset={-offset} />
          );
          offset += len;
          return node;
        }) : null}
      </Box>
      <Box
        component="ul"
        sx={{ m: 0, p: 0, listStyle: 'none', flex: '1 1 0', minWidth: 0, display: 'grid', gridTemplateColumns: 'max-content minmax(0, 1fr) max-content', columnGap: 1, rowGap: 0.9, alignItems: 'center' }}
      >
        {segments.map((s, i) => (
          <Box component="li" key={s.key} sx={{ display: 'contents' }} data-testid={testId ? `${testId}-${s.key}` : undefined}>
            <Box sx={{ width: 10, height: 10, borderRadius: '3px', bgcolor: DONUT_COLORS[i % DONUT_COLORS.length] }} aria-hidden />
            <Typography sx={{ ...metaAdsBodyTextSx, fontSize: '13px' }}>{s.label}</Typography>
            <Typography sx={{ ...metaAdsBodyTextSx, fontSize: '13px', fontWeight: 600, textAlign: 'right', whiteSpace: 'nowrap' }}>{s.detail}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

/**
 * Tabla simple con encabezado chico en mayúsculas. `columns`: `[{ key, label, align? }]` (la primera
 * es el nombre, ocupa el resto del ancho); `rows`: `{ key, cells: { [colKey]: string } }`.
 */
export function MetaTable({ columns, rows, testId }) {
  const template = columns.map((c, i) => (i === 0 ? 'minmax(0, 1fr)' : 'max-content')).join(' ');
  const headSx = { fontSize: '11px', letterSpacing: '0.04em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.45)', fontFamily: typography.fontFamily.primary, py: 0.75 };
  return (
    <Box component="table" sx={{ display: 'grid', gridTemplateColumns: template, m: 0, p: 0, width: '100%' }} data-testid={testId}>
      <Box component="thead" sx={{ display: 'contents' }}>
        <Box component="tr" sx={{ display: 'contents' }}>
          {columns.map((c, i) => (
            <Box component="th" key={c.key} scope="col" sx={{ ...headSx, textAlign: i === 0 ? 'left' : 'right', fontWeight: 500, borderBottom: dividerSx, pl: i === 0 ? 0 : 2 }}>{c.label}</Box>
          ))}
        </Box>
      </Box>
      <Box component="tbody" sx={{ display: 'contents' }}>
        {rows.map((r) => (
          <Box component="tr" key={r.key} sx={{ display: 'contents' }} data-testid={testId ? `${testId}-${r.key}` : undefined}>
            {columns.map((c, i) => (
              <Box component="td" key={c.key} sx={{ ...metaAdsBodyTextSx, fontSize: '13px', py: 1, textAlign: i === 0 ? 'left' : 'right', whiteSpace: i === 0 ? 'normal' : 'nowrap', overflowWrap: 'anywhere', borderBottom: dividerSx, pl: i === 0 ? 0 : 2 }}>
                {r.cells[c.key]}
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

/**
 * Filas «nombre completo → valor» con separador fino. `rows`: `{ key, label, value, hint? }`.
 * `hint` = ayuda corta que va debajo del nombre.
 */
export function MetaStatRows({ rows, testId }) {
  return (
    <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) max-content' }} data-testid={testId}>
      {rows.map((r) => (
        <Box key={r.key} sx={{ display: 'contents' }} data-testid={testId ? `${testId}-${r.key}` : undefined}>
          <Box component="dt" sx={{ ...metaAdsBodyTextSx, fontSize: '13px', py: 0.9, pr: 2, borderBottom: dividerSx }}>
            {r.label}
            {r.hint ? <Box component="span" sx={{ display: 'block', ...captionSx }}>{r.hint}</Box> : null}
          </Box>
          <Box component="dd" sx={{ ...metaAdsBodyTextSx, fontSize: '13px', fontWeight: 600, m: 0, py: 0.9, textAlign: 'right', whiteSpace: 'nowrap', borderBottom: dividerSx }}>
            {r.value}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

/** Subtítulo en mayúsculas dentro de una tarjeta («REACCIONES Y COMENTARIOS»). */
export function MetaGroupLabel({ children }) {
  return (
    <Typography component="h4" sx={{ fontSize: '11px', letterSpacing: '0.05em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.45)', fontWeight: 500, mt: 0.5 }}>
      {children}
    </Typography>
  );
}

/** Mosaico con un número grande y su nombre completo debajo. */
export function MetaStatTile({ value, label, testId }) {
  return (
    <Box sx={{ flex: 1, minWidth: 0, borderRadius: '10px', bgcolor: 'rgba(255,255,255,0.05)', px: 1.5, py: 1.25 }} data-testid={testId}>
      <Typography sx={{ fontSize: '22px', lineHeight: '26px', fontWeight: 600, color: colors.textPanelTitle, fontFamily: typography.fontFamily.primary }}>{value}</Typography>
      <Typography sx={{ ...captionSx, mt: 0.25 }}>{label}</Typography>
    </Box>
  );
}

/** Aviso chico (ámbar suave) para datos incompletos. */
export function MetaSoftNotice({ children, testId }) {
  return (
    <Box
      role="note"
      data-testid={testId}
      sx={{ borderRadius: '8px', px: 1.25, py: 1, bgcolor: alpha(colors.warning, 0.08), border: `1px solid ${alpha(colors.warning, 0.22)}`, ...metaAdsBodyTextSx, fontSize: '13px', lineHeight: '18px' }}
    >
      {children}
    </Box>
  );
}
